// Executive insights for the Instructors page.
// Derives Today's Focus signals using the SHARED readiness + missing helpers
// so the page never disagrees with the directory or MAVIS.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  getInstructorMissingRequirements,
  type MissingInstructor,
  type MissingAssignment,
} from "@/lib/instructorMissing";
import { checkInstructorReadiness } from "@/lib/instructorReadiness";

export type InstructorRow = MissingInstructor & {
  full_name: string;
  preferred_name: string | null;
  ready_to_teach_override?: boolean | null;
  updated_at?: string | null;
};

export type InstructorInsights = {
  loading: boolean;
  activeCount: number;
  readyCount: number;
  needsCareCount: number;
  missingAssignment: InstructorRow[];
  missingPay: InstructorRow[];
  missingW9: InstructorRow[];
  temporaryEndingSoon: { instructor_id: string; instructor_name: string; end_date: string }[];
  duplicateCount: number;
  prospects: InstructorRow[];
  recentlyReady: number;
  proposalsWaitingCount: number;
};

const DAY = 24 * 60 * 60 * 1000;

export function useInstructorInsights(): InstructorInsights {
  const [loading, setLoading] = useState(true);
  const [instructors, setInstructors] = useState<InstructorRow[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [dupes, setDupes] = useState<any[]>([]);
  const [proposals, setProposals] = useState<any[]>([]);

  const load = async () => {
    const [ins, asn, dup, prop] = await Promise.all([
      supabase.from("instructors")
        .select("id,full_name,preferred_name,status,email,phone,pay_rate_type,pay_rate_amount,hourly_rate,payment_method,zelle_name,zelle_contact,teaching_agreement_signed,teaching_agreement_signed_date,teaching_agreement_year,background_check_completed,background_check_date,background_check_year,standards_of_conduct_acknowledged,independent_contractor_ack,w9_status,onboarding_status,ready_to_teach_override,updated_at")
        .neq("status", "merged"),
      supabase.from("instructor_class_assignments" as any)
        .select("id,instructor_id,class_slug,class_title,status,assignment_kind,temporary_until_replaced,end_date"),
      // Admin-only wrapper: the raw suggest_instructor_duplicates() is no longer
      // executable by `authenticated` after the M3 grant hardening.
      (async () => { try { return await (supabase.rpc as any)("admin_suggest_instructor_duplicates", { _limit: 50 }); } catch { return { data: [] }; } })(),
      supabase.from("instructor_proposals")
        .select("id,status,updated_at")
        // "New" is what the public application writes — it must count as waiting.
        .in("status", ["New", "submitted", "in_review"]),

    ]);
    setInstructors((ins.data ?? []) as any);
    setAssignments((asn.data ?? []) as any);
    setDupes(((dup as any)?.data ?? []) as any);
    setProposals((prop.data ?? []) as any);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel(`instructor-insights-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "instructors" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "instructor_class_assignments" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "instructor_proposals" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo<InstructorInsights>(() => {
    const asnByInstructor = new Map<string, MissingAssignment[]>();
    for (const a of assignments) {
      const arr = asnByInstructor.get(a.instructor_id) ?? [];
      arr.push({ instructor_id: a.instructor_id, status: a.status });
      asnByInstructor.set(a.instructor_id, arr);
    }
    const active = instructors.filter((i) => i.status === "active" || i.status === "approved");
    const prospects = instructors.filter((i) => i.status === "prospect");
    const missingAssignment: InstructorRow[] = [];
    const missingPay: InstructorRow[] = [];
    const missingW9: InstructorRow[] = [];
    let readyCount = 0;

    for (const i of active) {
      const asn = asnByInstructor.get(i.id) ?? [];
      const missing = getInstructorMissingRequirements(i, asn);
      const readiness = checkInstructorReadiness(i as any, {
        hasAssignment: asn.some(a => ["active","upcoming","proposed"].includes(a.status ?? "")),
      });
      if (readiness.ready) readyCount += 1;
      if (missing.includes("class assignment")) missingAssignment.push(i);
      if (missing.includes("pay rate") || missing.includes("pay arrangement") || missing.includes("payment method")) {
        missingPay.push(i);
      }
      if (missing.includes("W-9")) missingW9.push(i);
    }

    const now = Date.now();
    const temporaryEndingSoon = assignments
      .filter((a: any) =>
        (a.assignment_kind === "temporary" || a.status === "temporary") &&
        a.end_date && new Date(a.end_date).getTime() - now < 14 * DAY &&
        new Date(a.end_date).getTime() >= now - DAY,
      )
      .map((a: any) => ({
        instructor_id: a.instructor_id,
        instructor_name:
          instructors.find((i) => i.id === a.instructor_id)?.full_name ?? "Instructor",
        end_date: a.end_date,
      }));

    const recentlyReady = active.filter(
      (i) => new Date(i.updated_at ?? 0).getTime() > now - 7 * DAY,
    ).length;

    return {
      loading,
      activeCount: active.length,
      readyCount,
      needsCareCount: active.length - readyCount,
      missingAssignment,
      missingPay,
      missingW9,
      temporaryEndingSoon,
      duplicateCount: dupes.length,
      prospects,
      recentlyReady,
      proposalsWaitingCount: proposals.length,
    };
  }, [instructors, assignments, dupes, proposals, loading]);
}
